import { useState, useRef, useCallback } from 'react';
import { HEADER_WIDTH } from './constants';
import { timeToMinutes, addMinutesToTime } from './utils';
import { doesOverlapExistingTemplate } from './validation';
import { getNowInSelectedTZParts } from './timeZoneHelpers';
import { DragState, DragVisual, SelectionMask } from './types';
import { getWeekdayIndex, handleDragDown, handleDragUp } from '../../../utils/fuctionsToCalendar';

interface UseDragSelectionProps {
  editMode: boolean;
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[];
  startOfWeek: Date;
  now: Date;
  selectedTimeZone: string;
  rowHeightPx: number;
  quarterHeightPx: number;
}

export function useDragSelection({ editMode, template, startOfWeek, selectedTimeZone, rowHeightPx, quarterHeightPx }: UseDragSelectionProps) {
  const [dragging, setDragging] = useState<DragState | null>(null);
  const [dragVisual, setDragVisual] = useState<DragVisual | null>(null);
  const [selectionMask, setSelectionMask] = useState<SelectionMask | null>(null);
  const [dragBlocked, setDragBlocked] = useState<boolean>(false);
  const [dragBlockedReason, setDragBlockedReason] = useState<string | null>(null);

  const dragRuntimeRef = useRef<{
    dragging: DragState | null;
    dragVisual: DragVisual | null;
    blocked: boolean;
    reason: string | null;
  } | null>(null);
  const rafRef = useRef<number | null>(null);

  const syncStateFromRef = () => {
    if (!dragRuntimeRef.current) return;

    setDragging(dragRuntimeRef.current.dragging);
    setDragVisual(dragRuntimeRef.current.dragVisual);
    setDragBlocked(dragRuntimeRef.current.blocked);
    setDragBlockedReason(dragRuntimeRef.current.reason);

    rafRef.current = null;
  };

  const dragInitStartMinRef = useRef<number | null>(null);
  const dragInitDurationMinRef = useRef<number | null>(null);

  const remToPx = (rem: number): number => {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return rem * fontSize;
  };

  const headerWidthPx = remToPx(HEADER_WIDTH);

  const resetDrag = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    dragRuntimeRef.current = null;

    setDragging(null);
    setDragVisual(null);
    setSelectionMask(null);
    setDragBlocked(false);
    setDragBlockedReason(null);
    dragInitStartMinRef.current = null;
    dragInitDurationMinRef.current = null;
  }, []);

  const handleMouseDown = useCallback((params: {
    date: string;
    timeSlot: string;
    weekdayIdx: number;
    gridRect: DOMRect;
    headerHeight: number;
    availabilityLength: number;
    clientY: number;
    adjustedStart: string;
    scrollTop: number;
    gridClientWidth: number;
  }) => {
    const { date, adjustedStart, weekdayIdx, gridRect, headerHeight, availabilityLength, scrollTop, gridClientWidth } = params;

    if (editMode) {
      const weekdayLocalIdxDrag = getWeekdayIndex(date, startOfWeek);
      const startMin = timeToMinutes(adjustedStart);
      const endMin = startMin + 60;

      if (doesOverlapExistingTemplate(template, weekdayLocalIdxDrag, startMin, endMin)) {
        resetDrag();
        setDragBlocked(true);
        setDragBlockedReason('Este rango se solapa con otro horario existente');
        return;
      }
    }

    const y = params.clientY - gridRect.top;
    let yAdj = y - headerHeight;
    yAdj = Math.max(0, Math.min(yAdj, gridRect.height - headerHeight));
    const rowIdxFromAdj = Math.floor(yAdj / rowHeightPx);
    const posInRow = yAdj - rowIdxFromAdj * rowHeightPx;
    const quarterIndex = Math.floor(Math.max(0, Math.min(posInRow, rowHeightPx - 1)) / quarterHeightPx);
    // Sumamos scrollTop: los overlays se desplazan con el contenido del grid,
    // pero la posición del cursor es relativa al viewport.
    const startY = headerHeight + rowIdxFromAdj * rowHeightPx + quarterIndex * quarterHeightPx + scrollTop;

    dragInitStartMinRef.current = timeToMinutes(adjustedStart);
    dragInitDurationMinRef.current = 60;
    setDragging({ date, weekdayIdx, startTime: adjustedStart, endTime: addMinutesToTime(adjustedStart, 60) });
    setDragVisual({ startY, currentY: startY + rowHeightPx });
    setDragBlocked(false);

    const days = Math.max(1, availabilityLength);
    // gridClientWidth excluye el scrollbar vertical (gridRect.width lo incluye),
    // así el ancho de columna coincide con el reparto real de la grilla.
    const dayW = (gridClientWidth - headerWidthPx) / days;
    const leftWidth = headerWidthPx + weekdayIdx * dayW;
    const rightLeft = headerWidthPx + (weekdayIdx + 1) * dayW;
    setSelectionMask({ leftWidth, rightLeft });

    dragRuntimeRef.current = {
      dragging: {
        date,
        weekdayIdx,
        startTime: adjustedStart,
        endTime: addMinutesToTime(adjustedStart, 60)
      },
      dragVisual: {
        startY,
        currentY: startY + rowHeightPx
      },
      blocked: false,
      reason: null
    };
  }, [editMode, template, startOfWeek, resetDrag, rowHeightPx, quarterHeightPx, headerWidthPx]);

  const handleMouseMove = useCallback((params: {
    snappedTop: number;
    baseTimeSlot: string;
    quarterIndex: number;
    headerHeight: number;
    visibleTimeSlots: string[];
  }) => {
    if (!dragging) return;

    const { snappedTop, baseTimeSlot, quarterIndex } = params;
    const tzNow = getNowInSelectedTZParts(selectedTimeZone);
    const todayStr = tzNow.todayStr;

    if (editMode) {
      const baseHourStr = baseTimeSlot.split(':')[0];
      const quarterMinutes = ['00', '30'][quarterIndex] || '00';
      const pointerTime = `${baseHourStr}:${quarterMinutes}`;
      const dateStrDrag = dragging?.date || todayStr;
      const initStartMin = dragInitStartMinRef.current !== null ? dragInitStartMinRef.current : timeToMinutes(dragging?.startTime || pointerTime);
      const initDurationMin = dragInitDurationMinRef.current !== null ? dragInitDurationMinRef.current : 60;
      const pointerHourNum = parseInt(baseHourStr, 10);
      const initStartHour = Math.floor(initStartMin / 60);
      const pointerMinRaw = timeToMinutes(pointerTime);

      if (initStartMin >= 0 && initStartMin < 60 && pointerMinRaw >= 23 * 60) {
        resetDrag();
        return;
      }

      if (pointerHourNum < initStartHour) {
        resetDrag();
        return;
      }

      const weekdayLocalIdxDrag = getWeekdayIndex(dateStrDrag, startOfWeek);

      const result = pointerMinRaw >= initStartMin
      ? handleDragDown({
          initStartMin,
          pointerMinRaw,
          weekdayIdx: weekdayLocalIdxDrag,
          template,
          dragVisual,
          snappedTop,
          rowHeightPx,
          quarterHeightPx
        })
      : handleDragUp({
          initStartMin,
          initDurationMin,
          pointerMinRaw,
          weekdayIdx: weekdayLocalIdxDrag,
          template,
          dragVisual,
          snappedTop,
          quarterHeightPx
        });

        const runtime = dragRuntimeRef.current;
        if (!runtime || !runtime.dragging) return;

        runtime.dragVisual = {
          startY: result.startY,
          currentY: result.currentY
        };

        
        runtime.dragging = {
          ...runtime.dragging,
          startTime: result.startTime,
          endTime: result.endTime
        };
        
        runtime.blocked = result.blocked;
        runtime.reason = result.reason;
    }
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(syncStateFromRef);
    }
  }, [dragging, editMode, template, startOfWeek, selectedTimeZone, dragVisual, resetDrag, rowHeightPx, quarterHeightPx]);

  return {
    dragging,
    dragVisual,
    selectionMask,
    dragBlocked,
    dragBlockedReason,
    handleMouseDown,
    handleMouseMove,
    resetDrag,
    setDragging,
    setDragVisual,
    setDragBlocked
  };
}

